<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="600px"
  >
    <template v-slot:activator="{ on }">
      <v-btn 
        icon 
        color="primary"
        v-on="on"
      >
        <v-icon>
          mdi-pencil
        </v-icon>
      </v-btn>
    </template>

    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title="Job Group Policy Claims"
      text="Edit Claim Policy"
      class="mb-0"
    >
      <form v-if="dialog && !loading">
        <v-container>
          <v-row no-gutters>
            <v-col cols="12">
              <v-text-field
                v-model="localJobGroupPolicy.name"
                :error-messages="errors.collect('name')"
                data-vv-name="name"                
                label="Name"
                readonly
                disabled
                outlined
              />
            </v-col>
            
            <v-col cols="12">
              <v-autocomplete
                v-model="localJobGroupPolicy.cutoff_type_id"
                :error-messages="errors.collect('cutoffType')"
                data-vv-name="cutoffType"
                data-vv-as="cutoff type"
                :items="cutoffTypes"
                outlined
                hide-selected
                item-text="name"
                item-value="id"
                label="Cutoff Type"
                clearable
                :hint="!localJobGroupPolicy.cutoff_type_id ? 'Select cutoff type if this policy\'s claims are through system' : ''"
                persistent-hint
              />
            </v-col>

            <v-col 
              v-if="localJobGroupPolicy.cutoff_type_id"
              cols="12"
            >
              <v-autocomplete
                v-model="claimSelections"
                v-validate="localJobGroupPolicy.cutoff_type_id ? 'required|duplicateClaimType' : ''"
                :error-messages="errors.collect('claimSelections')"
                data-vv-name="claimSelections"
                data-vv-as="claims"
                :items="claims"
                multiple
                chips
                hide-no-data
                outlined
                hide-selected
                item-text="name"
                item-value="id"
                deletable-chips
                label="Claims"
              />
            </v-col>

            <v-col 
              v-if="jobGroupPolicy.job_group_policy_claims.length <= 0"
              cols="12"
            >
              <v-menu
                v-model="startDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="startDate"
                    v-validate="'required'"
                    label="Claims Start Date"
                    :error-messages="errors.collect('startDate')"
                    data-vv-name="startDate"  
                    data-vv-as="start date"    
                    readonly
                    outlined
                    class="label-position"
                    v-on="on"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon>
                        mdi-calendar
                      </v-icon>
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="startDate"
                  :min="startDate"
                  no-title
                  @input="startDateMenu = false"
                />
              </v-menu>
            </v-col>
          </v-row>
        </v-container>
      </form>

      <v-card-actions>
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          class="px-10"  
          @click="dialog = false"
        >
          Close
        </v-btn>
        <v-btn
          :loading="progressing"
          color="hrissecondary"          
          class="white--text px-10"
          @click="submitForm"  
        >
          Update
        </v-btn>
      </v-card-actions>

      <v-dialog 
        v-model="confirmDialog" 
        max-width="500px"
      >
        <v-card>
          <v-card-title>
            Confirm Changes
          </v-card-title>
          <v-card-text v-html="changeMessage" />
          <v-card-actions>
            <v-spacer />
            <v-btn 
              text 
              @click="confirmDialog = false"
            >
              Cancel
            </v-btn>
            <v-btn 
              color="hrissecondary"
              class="white--text"
              @click="confirmDialog = false; savePolicy()"
            >
              OK
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </material-card>
  </v-dialog>
</template>

<script>
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      jobGroupPolicy: {
        type: Object,
        default: null,
      },
    },
    data () {
      return {
        dialog: null,
        loading: true,
        progressing: false,
        cutoffTypes: [],
        claims: [],
        claimSelections: [],
        localJobGroupPolicy: [],
        today: new Date().toISOString().slice(0, 10),
        startDateMenu: false,
        startDate: new Date(new Date().setDate(new Date().getDate() + 1)).toISOString().slice(0, 10),
        originalClaimSelections: [],
        confirmDialog: false,
        changeMessage: '',
      }
    },
    watch: {
      dialog: function (value) {
        if (value) {
          this.loadData()

          this.localJobGroupPolicy = { ...this.jobGroupPolicy }
          this.claimSelections = this.jobGroupPolicy.job_group_policy_claims.map(claim => {
            const settingId = claim.claimable.claim_type_id ? `${claim.claimable.claim_type_id}_${claim.claimable_id}` : claim.claimable_id
            return settingId
          })

          this.originalClaimSelections = [...this.claimSelections]
          this.loading = false

          this.$validator.extend('duplicateClaimType', {
            getMessage (field, args) {
              return 'No duplicate claim type allowed'
            },
            validate (value) {
              if (!Array.isArray(value)) return true

              const claimTypeIds = value.map(v => {
                if (typeof v === 'string' && v.includes('_')) {
                  return parseInt(v.split('_')[0])
                }

                return parseInt(v)
              }).filter(Boolean)

              const hasDuplicate = new Set(claimTypeIds).size !== claimTypeIds.length
              return !hasDuplicate
            },
          })
        }
      },
    },
    methods: {
      loadData () {
        Promise.all([
          this.$api.cutoffTypes().list(),
          this.$api.claimTypes().list(),
          this.$api.claimEntitlementSettings().list(),
        ]).then(([cutoffTypes, claimTypes, claimEntitlementSettings]) => {
          this.cutoffTypes = _.orderBy(cutoffTypes.data, ['code'], ['asc'])

          let mergedClaims = []
          claimTypes.data.forEach(claimType => {
            const settings = claimEntitlementSettings.data.filter(setting => setting.claim_type_id === claimType.id)
            
            if (settings.length > 0) {
              settings.forEach(setting => {
                mergedClaims.push({
                  ...claimType,
                  id: `${claimType.id}_${setting.id}`, // Ensure uniqueness by appending claim entitlement setting ID
                  name: setting.name, 
                  credit_unit: setting.credit_unit,
                })
              })
            } else {
              if (claimType.has_limit === 0) {
                mergedClaims.push(claimType)
              }
            }
          })

          this.claims = mergedClaims.sort((a, b) => a.name.localeCompare(b.name))
        })
      },
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            if (this.jobGroupPolicy.job_group_policy_claims.length > 0) {
              const addedClaims = this.claimSelections.filter(id => !this.originalClaimSelections.includes(id))
              const removedClaims = this.originalClaimSelections.filter(id => !this.claimSelections.includes(id))

              if (addedClaims.length || removedClaims.length) {
                let messages = []
                if (addedClaims.length) {
                  const addedNames = addedClaims
                    .map(id => {
                      const claim = this.claims.find(c => c.id === id)
                      return `<li>${claim ? claim.name : id}</li>`
                    })
                    .join('\n')
                  messages.push(`The following claims will start on ${this.$moment().add(1, 'day').format('YYYY-MM-DD')}: <ul>${addedNames}</ul>`)
                }

                if (removedClaims.length) {
                  const removedNames = removedClaims
                    .map(id => {
                      const claim = this.claims.find(c => c.id === id)
                      return `<li>${claim ? claim.name : id}</li>`
                    })
                    .join('\n')
                  messages.push(`The following claims will end on ${this.today}: <ul>${removedNames}</ul>`)
                }

                messages.push(`<p style="font-style:italic;">
                  Note: The start or end date for these claims can be updated later in the table if necessary. Claim with future start date that being removed will be deleted.
                </p>`)

                this.changeMessage = messages.join('<br>')
                this.confirmDialog = true
              }
            } else {
              this.savePolicy()
            }
          })
      },
      savePolicy () {
        this.progressing = true

        this.$api.claimPolicies().update(this.jobGroupPolicy.id, this.prepareFormData())
          .then(response => {
            if (response.status >= 200 && response.status < 300) {
              this.$emit('updated')
              this.$snack({ message: response.data.status })

              this.dialog = false
            }
          })
          .catch(() => {
            this.$snack({ 
              message: 'Unable to update claim policy',
              color: 'error',
            }) 
          })
          .then(() => {
            this.progressing = false
          })
      },
      prepareFormData () {
        return {
          cutoff_type_id: this.localJobGroupPolicy.cutoff_type_id,
          claims: this.claimSelections,
          start_date: this.jobGroupPolicy.job_group_policy_claims.length <= 0 ? this.startDate : null,
        }
      },
    },
  }
</script>

<style scoped>
.label-position >>> .v-label.v-label--active{
  margin-left:-28px;
  background:white;
  padding-right:5px
}
</style>
